// Operations console contracts (operations-console stream, ADR-029): site operating calendar and
// rules (PB-12-01), reception escalations (PB-06-07, PB-06-15), central multi-site batches
// (PB-12-06) and per-topic background-job figures (PB-19-07).
import { z } from 'zod';

const Uuid = z.uuid();
const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time');
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const reason = z.string().trim().min(3, 'v.opsReasonShort').max(500, 'v.tooLong');
const ImpactToken = z.string().regex(/^[A-Za-z0-9_-]{10,64}$/, 'v.opsuiImpactToken');

export const WeeklyHours = z.array(z.object({ day: z.number().int().min(1).max(7), open: Time, close: Time })).max(28);
export type WeeklyHours = z.infer<typeof WeeklyHours>;

// ------------------------------------------------------------------ operating hours and closures (PB-12-01)
export const HoursImpactRequest = z.object({ operatingHours: WeeklyHours });

export const UpdateOperatingHoursRequest = z.object({
  expectedVersion: z.number().int().min(1),
  operatingHours: WeeklyHours,
  /** The impact token the person reviewed; required when the change affects future visits. */
  impactToken: ImpactToken.optional(),
});
export type UpdateOperatingHoursRequest = z.infer<typeof UpdateOperatingHoursRequest>;

export const CLOSURE_KINDS = ['holiday', 'closure', 'reduced_hours'] as const;
export const ClosureDraft = z.object({
  kind: z.enum(CLOSURE_KINDS),
  startsOn: LocalDate,
  endsOn: LocalDate,
  label: z.string().trim().min(1, 'v.required').max(120, 'v.tooLong'),
  open: Time.nullable().optional(),
  close: Time.nullable().optional(),
});
export type ClosureDraft = z.infer<typeof ClosureDraft>;
export const ClosureImpactRequest = ClosureDraft;
export const CreateClosureRequest = ClosureDraft.extend({ impactToken: ImpactToken.optional() });
export const RemoveClosureRequest = z.object({ reason });

export interface ClosureResponse {
  readonly id: string;
  readonly kind: (typeof CLOSURE_KINDS)[number];
  readonly startsOn: string;
  readonly endsOn: string;
  readonly label: string;
  readonly open: string | null;
  readonly close: string | null;
  readonly createdAt: string;
  readonly createdBy: string | null;
}

export interface HoursImpactResponse {
  readonly siteId: string;
  readonly timeZone: string;
  /** Present the token back with the change; it changes when the affected set changes. */
  readonly impactToken: string;
  readonly affectedCount: number;
  /** First affected visits, earliest first (reference and window only — no visitor details). */
  readonly affected: ReadonlyArray<{ readonly visitId: string; readonly reference: string; readonly startsAt: string; readonly endsAt: string; readonly reason: 'closure' | 'closed_day' | 'outside_hours' }>;
  readonly alreadyOutside: number;
  /** True when more future visits exist than were examined. */
  readonly truncated: boolean;
}

// ------------------------------------------------------------------ operating rules (PB-06-07, PB-06-15)
export const UpdateOperatingRulesRequest = z
  .object({
    /** 0 when no rules exist yet for the site. */
    expectedVersion: z.number().int().min(0),
    waitingEscalationEnabled: z.boolean(),
    waitingHostReminderMinutes: z.number().int().min(1).max(240).nullable(),
    waitingFallbackMinutes: z.number().int().min(2).max(480).nullable(),
    fallbackUserId: Uuid.nullable(),
    overstayNoticesEnabled: z.boolean(),
    siteOwnerUserId: Uuid.nullable(),
  })
  .refine((r) => r.waitingFallbackMinutes === null || r.waitingHostReminderMinutes === null || r.waitingFallbackMinutes > r.waitingHostReminderMinutes, { path: ['waitingFallbackMinutes'], message: 'v.opsuiFallbackAfterHost' });
export type UpdateOperatingRulesRequest = z.infer<typeof UpdateOperatingRulesRequest>;

export interface OperatingRulesResponse {
  readonly siteId: string;
  /** 0 = no rules saved; the effective values are the documented defaults. */
  readonly version: number;
  readonly configured: {
    readonly waitingEscalationEnabled: boolean;
    readonly waitingHostReminderMinutes: number | null;
    readonly waitingFallbackMinutes: number | null;
    readonly fallbackUserId: string | null;
    readonly overstayNoticesEnabled: boolean;
    readonly siteOwnerUserId: string | null;
  };
  readonly effective: { readonly waitingEscalationEnabled: boolean; readonly waitingHostReminderMinutes: number; readonly waitingFallbackMinutes: number; readonly overstayNoticesEnabled: boolean };
  readonly defaults: { readonly waitingHostReminderMinutes: number; readonly waitingFallbackMinutes: number };
  readonly updatedAt: string | null;
}

export interface SiteOperationsResponse {
  readonly siteId: string;
  readonly timeZone: string;
  readonly siteVersion: number;
  readonly operatingHours: WeeklyHours;
  readonly closures: readonly ClosureResponse[];
  readonly rules: OperatingRulesResponse;
  /** People who may be named as reception lead or site owner (active staff with reception access to the site). */
  readonly contacts: ReadonlyArray<{ readonly id: string; readonly displayName: string }>;
  readonly canEdit: boolean;
}

// ------------------------------------------------------------------ reception escalations (PB-06-07, PB-06-15)
export const EscalationsQuery = z.object({ siteId: Uuid, participationId: Uuid.optional(), limit: z.coerce.number().int().min(1).max(200).default(50) });

export interface EscalationResponse {
  readonly id: string;
  readonly kind: 'waiting_host' | 'waiting_fallback' | 'overstay';
  readonly participationId: string;
  readonly visitId: string;
  readonly thresholdMinutes: number | null;
  readonly dueAt: string;
  readonly raisedAt: string;
  /** Delivery facts of the notices raised for it (status only; never message content). */
  readonly notices: ReadonlyArray<{ readonly status: string; readonly channel: string; readonly reason: string | null }>;
}

// ------------------------------------------------------------------ central batches (PB-12-06)
export const SITE_BATCH_KINDS = ['operating_hours', 'operating_rules', 'branding_colour'] as const;
export type SiteBatchKind = (typeof SITE_BATCH_KINDS)[number];

export const BatchRulesChange = z
  .object({
    waitingEscalationEnabled: z.boolean().optional(),
    waitingHostReminderMinutes: z.number().int().min(1).max(240).nullable().optional(),
    waitingFallbackMinutes: z.number().int().min(2).max(480).nullable().optional(),
    overstayNoticesEnabled: z.boolean().optional(),
  })
  .refine((c) => Object.keys(c).length > 0, { message: 'v.required' });

export const SiteBatchChange = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('operating_hours'), operatingHours: WeeklyHours }),
  z.object({ kind: z.literal('operating_rules'), rules: BatchRulesChange }),
  z.object({ kind: z.literal('branding_colour'), primaryColour: z.string().trim().toLowerCase().regex(/^#[0-9a-f]{6}$/, 'v.colour') }),
]);
export type SiteBatchChange = z.infer<typeof SiteBatchChange>;

const SiteIds = z.array(Uuid).min(1, 'v.required').max(200, 'v.tooLong');

export const SiteBatchPreviewRequest = z.object({ change: SiteBatchChange, siteIds: SiteIds });

export const SiteBatchApplyRequest = z.object({
  change: SiteBatchChange,
  reason,
  sites: z
    .array(z.object({ siteId: Uuid, expectedVersion: z.number().int().min(0), impactToken: ImpactToken.optional() }))
    .min(1, 'v.required')
    .max(200, 'v.tooLong')
    .refine((s) => new Set(s.map((x) => x.siteId)).size === s.length, { message: 'v.opsuiDuplicateSite' }),
});
export type SiteBatchApplyRequest = z.infer<typeof SiteBatchApplyRequest>;

export type SiteBatchOutcome = 'applied' | 'unchanged' | 'forbidden' | 'not_found' | 'version_conflict' | 'impact_review_required' | 'invalid' | 'failed';
export type SitePreviewOutcome = 'ready' | 'unchanged' | 'impact_review_required' | 'forbidden' | 'not_found' | 'invalid';

export interface SiteBatchPreviewResponse {
  readonly kind: SiteBatchKind;
  readonly items: ReadonlyArray<{
    readonly siteId: string;
    /** Null when the site is not readable to the caller (indistinguishable from absent). */
    readonly siteName: string | null;
    readonly outcome: SitePreviewOutcome;
    /** The version to send back as expectedVersion (site, rules or branding version). */
    readonly currentVersion: number | null;
    readonly impact: { readonly affectedCount: number; readonly impactToken: string } | null;
    readonly problem: string | null;
  }>;
}

export interface SiteBatchResponse {
  readonly id: string;
  readonly kind: SiteBatchKind;
  readonly status: 'completed' | 'partial' | 'failed' | 'unchanged';
  readonly reason: string;
  readonly createdAt: string;
  readonly requestedBy: string;
  readonly change: Record<string, unknown>;
  readonly items: ReadonlyArray<{
    readonly siteId: string;
    readonly siteName: string | null;
    readonly outcome: SiteBatchOutcome;
    readonly previousVersion: number | null;
    readonly newVersion: number | null;
    readonly detail: Record<string, unknown>;
  }>;
}

export interface SiteBatchListResponse {
  readonly items: ReadonlyArray<{ readonly id: string; readonly kind: SiteBatchKind; readonly status: SiteBatchResponse['status']; readonly reason: string; readonly createdAt: string; readonly siteCount: number; readonly appliedCount: number }>;
}

// ------------------------------------------------------------------ per-topic job figures (PB-19-07)
export interface OperationsTopicsResponse {
  readonly generatedAt: string;
  readonly items: ReadonlyArray<{
    readonly topic: string;
    readonly effect: 'external' | 'internal' | 'none';
    readonly channel: string | null;
    /** Waiting to run now (queued + retrying whose time has come). */
    readonly due: number;
    readonly scheduled: number;
    readonly retrying: number;
    readonly inFlight: number;
    readonly deadLetter: number;
    /** Age of the oldest job that is due and not yet delivered. */
    readonly oldestDueAgeSeconds: number | null;
    readonly oldestDeadLetterAt: string | null;
  }>;
}
